import { useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";
import { TextPlugin } from "gsap/TextPlugin";

import "./AboutUs.scss";
import LayeredImage from "../../components/shared/LayeredImage";
import { useAsset } from "../../components/shared/AssetLoader";

import ImgAboutUsCloud from "../../assets/images/about-us-cloud.svg";
import ImgStormCharacter from "../../assets/images/storm-character.png";
import ImgShieldIcon from "../../assets/images/shield-icon.png";

import ImgLightMcp from "../../assets/images/light-mcp.svg";
import ImgGears from "../../assets/images/gears.png";
import ImgMcpLines from "../../assets/images/mcp-lines.png";
import FeatureCard from "../../components/Home/FeatureCard";
import BadgeChip from "../../components/Home/BadgeChip";

export default function AboutUs({ position }) {
	const aboutUsCloudAsset = useAsset(ImgAboutUsCloud);
	const stormCharacterAsset = useAsset(ImgStormCharacter);
	const shieldIconAsset = useAsset(ImgShieldIcon);
	const lightMcpAsset = useAsset(ImgLightMcp);
	const mcpLinesAsset = useAsset(ImgMcpLines);
	const gearsAsset = useAsset(ImgGears);

	const aboutUsImgRef = useRef();
	const aboutUsImgCharacterRef = useRef();
	const aboutUsImgHasAnimated = useRef(false);

	const mcpTitleRef = useRef();
	const mcpDescriptionRef = useRef();
	const mcpHasAnimated = useRef(false);

	const featureCardsRowRef = useRef();
	const featureCardsRowHasAnimated = useRef(false);

	useLayoutEffect(() => {
		// Animate the about us image when visible
		if (aboutUsImgRef.current) {
			const aboutUsImgRectBottom =
				aboutUsImgRef.current.offsetTop +
				aboutUsImgRef.current.offsetHeight;

			if (
				position >= aboutUsImgRectBottom - window.innerHeight * 0.8 && // 0.8 means when the image reaches 80% of the viewport height
				!aboutUsImgHasAnimated.current
			) {
				const tl = gsap.timeline();
				tl.to(aboutUsImgCharacterRef.current, {
					duration: 1,
					top: "88%", // animate to 88% from 68%
					ease: "power2.out",
				});

				aboutUsImgHasAnimated.current = true;
			}
		}

		// Animate MCP title when visible
		if (mcpTitleRef.current) {
			// calculate the position of the mcp title
			const mcpTitleRectBottom =
				mcpTitleRef.current.offsetTop +
				mcpTitleRef.current.offsetHeight;

			// start the animation when the about-us section becomes visible
			if (
				position >= mcpTitleRectBottom - window.innerHeight * 1.0 && // 1.0 means as soon as the title is in view
				!mcpHasAnimated.current
			) {
				const tl = gsap.timeline();
				tl.to(mcpTitleRef.current.querySelector(".bold-txt"), {
					duration: 0.6,
					text: "Model Context",
					ease: "none",
				})
					.to(mcpTitleRef.current.querySelector(".normal-txt"), {
						duration: 0.4,
						text: "Protocol",
						ease: "none",
					})
					.to(mcpDescriptionRef.current, {
						duration: 6,
						text: "MCP is the private intelligence core behind StormAI, a dynamic system that learns how you trade without ever knowing who you are. It builds a temporary, encrypted behavior profile based on your risk tolerance, token preferences, and movement patterns, all while remaining private. With this real-time context, StormAI can flag unusual activity, catch scams you’d normally avoid, and adjust protection based on your trading style. Whether you’re sniping new tokens or playing it safe, MCP fine-tunes filters, timing, and friction on the fly, delivering personalized AI defense that’s invisible, encrypted, and unlinkable by design.",
						ease: "none",
					});

				mcpHasAnimated.current = true;
			}
		}

		// Animate feature cards row when visible
		if (featureCardsRowRef.current) {
			const featureCardsRowRectBottom =
				featureCardsRowRef.current.offsetTop +
				featureCardsRowRef.current.offsetHeight;

			if (
				position >=
					featureCardsRowRectBottom - window.innerHeight * 0.9 && // 0.9 means when the row reaches 90% of the viewport height
				!featureCardsRowHasAnimated.current
			) {
				const cards = gsap.utils.toArray(
					featureCardsRowRef.current.querySelectorAll(".feature-card")
				);
				const tl = gsap.timeline();

				cards.forEach((card, index) => {
					// Calculate the absolute start time for this card's animation on the timeline.
					const startTime = index * 0.4;

					// Schedule the class addition and removal at specific times.
					tl.call(
						() => card.classList.add("active"),
						null,
						startTime
					);
					tl.call(
						() => card.classList.remove("active"),
						null,
						startTime + 0.5
					);
				});

				featureCardsRowHasAnimated.current = true;
			}
		}
	}, [position]);

	return (
		<>
			<div className="about-us">
				<div className="about-us-content-wrapper content-wrapper-lg">
					<div className="about-us-content">
						<LayeredImage
							className="about-us-img"
							width={1085}
							height={480}
							ref={aboutUsImgRef}
						>
							<img
								className="cloud"
								src={ImgAboutUsCloud}
								alt="About Us Cloud"
								onLoad={aboutUsCloudAsset.onLoad}
							/>

							<span className="storm-ai">StormAI</span>

							<div className="storm-character-wrapper">
								<img
									className="storm-character"
									src={ImgStormCharacter}
									alt="Storm Character"
									ref={aboutUsImgCharacterRef}
									onLoad={stormCharacterAsset.onLoad}
								/>
							</div>

							<div className="fader"></div>

							<BadgeChip className="storm-ai-chip">
								<img
									className="badge-chip-icon"
									src={ImgShieldIcon}
									alt="Shield Icon"
									onLoad={shieldIconAsset.onLoad}
								/>
								StormAI
							</BadgeChip>
						</LayeredImage>

						<div className="about-us-text">
							StormAI is StormBot’s encrypted AI privacy shield,
							protecting every trade by default. It filters scams,
							rugs, and high-risk tokens by analyzing contracts
							and copy-trade targets in real time. Each
							transaction, whether its a buy, sell, or snipe, is
							pre-screened and routed through stealth wallets and
							private relayers to keep activity hidden. StormAI
							also predicts MEV, frontrunning, and slippage,
							adjusting execution timing and routing for maximum
							protection. Just use{" "}
							<span className="command-txt">/ask</span> to speak
							to it in natural language and get private,
							contextual answers tailored to your trading
							activity.
						</div>

						<div className="about-us-items">
							<div className="about-us-item mcp">
								<div className="item-content">
									<h2
										className="item-title"
										ref={mcpTitleRef}
									>
										<span className="bold-txt"></span>{" "}
										<span className="normal-txt"></span>
									</h2>
									<div className="item-description-wrapper">
										<p className="item-description-hidden">
											MCP is the private intelligence core
											behind StormAI, a dynamic system
											that learns how you trade without
											ever knowing who you are. It builds
											a temporary, encrypted behavior
											profile based on your risk
											tolerance, token preferences, and
											movement patterns, all while
											remaining private. With this
											real-time context, StormAI can flag
											unusual activity, catch scams you’d
											normally avoid, and adjust
											protection based on your trading
											style. Whether you’re sniping new
											tokens or playing it safe, MCP
											fine-tunes filters, timing, and
											friction on the fly, delivering
											personalized AI defense that’s
											invisible, encrypted, and unlinkable
											by design.
										</p>
										<p
											className="item-description"
											ref={mcpDescriptionRef}
										></p>
									</div>
								</div>

								<LayeredImage
									className="item-img"
									width={600}
									height={600}
								>
									<img
										className="light-mcp"
										src={ImgLightMcp}
										alt="Light MCP"
										onLoad={lightMcpAsset.onLoad}
									/>

									<img
										className="mcp-lines"
										src={ImgMcpLines}
										alt="MCP Lines"
										onLoad={mcpLinesAsset.onLoad}
									/>

									<img
										className="gears"
										src={ImgGears}
										alt="Gears"
										onLoad={gearsAsset.onLoad}
									/>
								</LayeredImage>
							</div>
						</div>

						<div
							className="feature-cards-row"
							ref={featureCardsRowRef}
						>
							<div className="feature-card-col">
								<FeatureCard className="private-sniping">
									<div className="feature-card-content">
										<h6 className="feature-card-title">
											Private Sniping
										</h6>

										<p className="feature-card-description">
											StormBot privately snipes new token
											launches using fresh stealth wallets
											funded via our mixer. Transactions
											are sent through private relayers,
											preventing frontrunning and wallet
											exposure. After the snipe, tokens
											stay secure or move to new stealth
											wallets, while the used wallet is
											burned. You get encrypted
											confirmations via Telegram—total
											privacy, zero leaks.
										</p>
									</div>
								</FeatureCard>
							</div>

							<div className="feature-card-col">
								<FeatureCard className="private-copy-trading">
									<div className="feature-card-content">
										<h6 className="feature-card-title">
											Private Copy Trading
										</h6>

										<p className="feature-card-description">
											Copy trade any public wallet without
											exposing yours. StormBot monitors
											the target and executes mirrored
											trades through fresh stealth
											wallets, mixers, and private
											relayers. Set it once with{" "}
											<span className="command-txt">
												/copy [wallet] [amount]
											</span>
											, then watch your portfolio follow
											smart money—all while your activity
											stays completely invisible.
										</p>
									</div>
								</FeatureCard>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
